import { format as formatter } from 'date-fns'

/**
 * 日付を表現する型であり、とくに時間以降に興味がない場合に使用する。
 * カレンダUIなど、Date型が要求される場合には、時間以降について適当に正規化した内容のDate型を生成して利用する。
 */
export class DateWithoutHour {
  /**
   * 将来的には日付文字列のフォーマットも指定して、そのフォーマットに沿って日付として解釈できるか検証できるようにしたい。
   * 今はDate型が日付文字列として解釈できるかどうかに依存しているので、フォーマットはあえて渡していない。
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/parse にあるように、
   * Date型のパースはブラウザ実装依存なところがあるため、本当は避けたい。
   *
   * またdate-fnsも https://date-fns.org/v1.30.1/docs/parse の時点では、フォーマットを指定してのパース・Date型作成に対応していない。
   * v2以降は可能。
   */
  static create(date: string) {
    const d = createDate(date)

    if (isInvalidDate(d)) {
      throw new Error(`Invalid date string: date=${date}`)
    }

    return new DateWithoutHour(date)
  }

  /**
   * @param date 日付を表す文字列（Date型でフォーマット可能なもの）
   */
  private constructor(
    private readonly date: string
  ) {}

  get valueWithNormalizedHour(): Date {
    return new Date(
      new Date(createDate(this.date))
        .setHours(0, 0, 0, 0)
    )
  }

  get dateOfMonth(): number {
    return this.valueWithNormalizedHour.getDate()
  }

  formattedDate(format: string): string {
    return formatter(this.valueWithNormalizedHour, format)
  }

  /**
   * 同じ日付かどうかを返す。
   * 時間以降を正規化したDate型ベースで比較するため、日付を指定する元の文字列のフォーマットや、時間以降の値が互いに異なっていても、結果には影響しない。
   */
  isEqualTo(other: DateWithoutHour): boolean {
    return this.valueWithNormalizedHour.getTime() === other.valueWithNormalizedHour.getTime()
  }
}

/**
 * 将来的に、date-fnsのparseメソッドなどで、正しい日付型が指定されたフォーマットで生成できるか確認し、できなければ例外を投げる、
 * などの処理を入れる可能性があるため、一応独立のメソッドに切り出してある。
 */
function createDate(date: string): Date {
  return new Date(date)
}

function isInvalidDate(date: Date): boolean {
  return Number.isNaN(date.getTime())
}
